import { createSlice, current, WritableDraft } from "@reduxjs/toolkit";
import { IssuesChecker } from "../../server/types/index.js";
import {
  GetIssuesQueryParams,
  IssueItems,
  IssueItemType,
  IssuesState,
} from "../types/index.js";
import { fetch, IssueNotFoundError } from "../utils/utils.js";
import { showAlert } from "./alerts.js";
import { createAppAsyncThunk } from "./utils.js";

const sliceName = "issues";

const initialIssues: IssueItems = {
  byId: {},
  byType: {},
  allIds: [],
  prevIssueState: {},
};

const initialChecker: IssuesChecker = {
  issues: 0,
  critical: 0,
  errors: 0,
  warnings: 0,
  archived: 0,
  resolved: 0,
  lastRun: null,
  status: "ready",
};

const initialState: IssuesState = {
  issues: initialIssues,
  checker: initialChecker,
};

export const getIssues = createAppAsyncThunk(
  `${sliceName}/getIssues`,
  async (
    {
      siteId,
      params,
    }: { siteId: string; params?: GetIssuesQueryParams; keepExisting: boolean },
    { dispatch },
  ) => {
    try {
      const data = await fetch({ dispatch })
        .query(params ?? {}, { omitUndefinedOrNullValues: true })
        .get(`sites/${siteId}/seo/issues`)
        .json<IssueItemType[]>();

      return data;
    } catch (error) {
      dispatch(
        showAlert(
          "Das Laden der Liste mit den Fehlern / Warnungen ist fehlgeschlagen. Versuchen Sie es später erneut!",
        ),
      );
      throw error;
    }
  },
);

export const getIssuesCheck = createAppAsyncThunk(
  `${sliceName}/getIssuesCheck`,
  async (
    {
      siteId,
      showLastResult,
    }: { siteId: string; forceUpdate: boolean; showLastResult: boolean },
    { dispatch },
  ) => {
    try {
      const data = await fetch({ dispatch })
        .query({ showLastResult })
        .get(`sites/${siteId}/seo/issues/check`)
        .json<IssuesChecker>();
      return data;
    } catch (error) {
      dispatch(
        showAlert(
          "Das Laden der Daten zur Problembehebung ist fehlgeschlagen. Versuchen Sie es später erneut!",
        ),
      );
      throw error;
    }
  },
  {
    condition: ({ forceUpdate }, { getState }) => {
      const loadState = getState().loadStates.issues.checker;
      return forceUpdate || loadState === "unloaded";
    },
  },
);

export const postIssuesCheck = createAppAsyncThunk(
  `${sliceName}/postIssuesCheck`,
  async (siteId: string, { dispatch }) => {
    try {
      const data = await fetch({ dispatch })
        .post(null, `sites/${siteId}/seo/issues/check`)
        .json<IssuesChecker>();
      return data;
    } catch (error) {
      dispatch(
        showAlert(
          "Das Starten der Überprüfung ist fehlgeschlagen. Versuchen Sie es später erneut!",
        ),
      );
      throw error;
    }
  },
);

export const patchIssue = createAppAsyncThunk(
  `${sliceName}/patchIssue`,
  async (
    {
      siteId,
      issueId,
      archive,
    }: { siteId: string; issueId: string; archive: boolean },
    { dispatch },
  ) => {
    try {
      await fetch({ dispatch })
        .patch({ isArchived: archive }, `sites/${siteId}/seo/issues/${issueId}`)
        .res();
    } catch (error) {
      dispatch(
        showAlert(
          "Das Speichern des Elements ist fehlgeschlagen. Versuchen Sie es später erneut!",
        ),
      );
      throw error;
    }
  },
  {
    dispatchConditionRejection: true,
    condition: ({ issueId }, { getState }) =>
      !!getState().issues.issues.byId[issueId],
  },
);

const changeIssueArchivedState = (
  state: WritableDraft<IssuesState>,
  issueId: string,
  archive: boolean,
) => {
  const currentIssue = state.issues.byId[issueId];
  if (!currentIssue) throw new IssueNotFoundError(issueId);
  const { severity, isArchived } = currentIssue;
  const sameArchivedState = isArchived === archive;
  const isNowArchived = !isArchived && archive;

  currentIssue.isArchived = archive;
  state.checker.archived =
    state.checker.archived + (sameArchivedState ? 0 : isNowArchived ? 1 : -1);
  state.checker.errors =
    state.checker.errors +
    (sameArchivedState || severity !== "error" ? 0 : isNowArchived ? -1 : 1);
  state.checker.issues =
    state.checker.issues + (sameArchivedState ? 0 : isNowArchived ? -1 : 1);
  state.checker.warnings =
    state.checker.warnings +
    (sameArchivedState || severity !== "warning" ? 0 : isNowArchived ? -1 : 1);
};

const slice = createSlice({
  name: sliceName,
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(getIssues.fulfilled, (state, action) => {
      state.issues = action.payload.reduce<IssueItems>(
        (acc, item) => ({
          ...acc,
          allIds: acc.allIds.concat(item.id),
          byId: { ...acc.byId, [item.id]: item },
          byType: {
            ...acc.byType,
            [item.type]: (acc.byType[item.type] || []).concat(item.id),
          },
        }),
        initialIssues,
      );
    });

    builder.addCase(patchIssue.pending, (state, action) => {
      const { issueId, archive } = action.meta.arg;
      const issue = state.issues.byId[issueId];
      if (issue) {
        state.issues.prevIssueState[action.meta.requestId] = {
          ...current(issue),
        };
      }

      changeIssueArchivedState(state, issueId, archive);
    });

    builder.addCase(patchIssue.fulfilled, (state, action) => {
      delete state.issues.prevIssueState[action.meta.requestId];
    });

    builder.addCase(patchIssue.rejected, (state, action) => {
      const { issueId } = action.meta.arg;
      if (action.meta.condition) {
        throw new IssueNotFoundError(issueId);
      }

      const { requestId } = action.meta;
      const prevState = state.issues.prevIssueState[requestId];
      if (!prevState) return;

      delete state.issues.prevIssueState[requestId];
      state.issues.byId[issueId] = { ...prevState };
    });

    builder.addCase(getIssuesCheck.fulfilled, (state, action) => {
      state.checker = action.payload;
    });

    builder.addCase(postIssuesCheck.fulfilled, (state, action) => {
      state.checker = action.payload;
    });
  },
});

export default slice.reducer;
